import drivewebapi from "@/store/drivewebapi"

const uploadUtils = {
  prepareUploadParam(targetId, nodeInfo, conflictAction) {
    // Get the correct upload target file
    const getUploadTargetFile = (fileInfo) => {
      if (fileInfo.prefix && fileInfo.truncatedName !== fileInfo.name) {
        // In Windows, file name may exceed 255 characters, so we need to create a new file object with the correct name
        return new File([fileInfo.file], fileInfo.truncatedName, {
          type: fileInfo.file.type,
          lastModified: fileInfo.file.lastModified,
        })
      }
      return fileInfo.file
    }

    // Get the relative path of the file
    const getRelPath = (fileInfo) => {
      // file input selected folder
      if (fileInfo.relativePath) {
        if (fileInfo.prefix) {
          // relativePath needs to be truncated to avoid exceeding the maximum length of the file path in Windows
          if (fileInfo.truncatedName !== fileInfo.name) {
            const paths = fileInfo.relativePath.split('/')
            // change the first path to the truncated name
            paths[0] = fileInfo.truncatedName

            return `${fileInfo.prefix}_${paths.join('/')}`
          }

          return `${fileInfo.prefix}_${fileInfo.relativePath}`
        }

        return fileInfo.relativePath
      }

      // drag and drop folder or file input selected file
      if (fileInfo.prefix) {
        return fileInfo.prefixName || fileInfo.name || fileInfo.file.name
      }

      return fileInfo.name || fileInfo.file.name
    }

    const fileInfo = {
      file: nodeInfo.file || nodeInfo.content,
      // Use shiftedRelativePath to upload the file to the correct folder
      relativePath: nodeInfo.shiftedRelativePath || nodeInfo.relativePath || nodeInfo.webkitRelativePath,
      prefix: nodeInfo.prefix,
      prefixName: nodeInfo.prefixName,
      truncatedName: nodeInfo.truncatedName,
    }

    return {
      uploadTargetFile: getUploadTargetFile(fileInfo),
      relativePath: getRelPath(fileInfo),
      lastModified: fileInfo.file.lastModified,
      targetId: targetId, // target folder id
      conflictAction: conflictAction,
      uploadId: nodeInfo.uploadId
    }
  },

  async uploadDryRun(uploadParam) {
    uploadParam.isDryRun = true
    uploadParam.fileContent = new Blob([]) // empty file for dry run

    const sleep = (ms) => {
      return new Promise((resolve) => setTimeout(resolve, ms))
    }

    // Retry loop
    for (;;) {
      try {
        return await drivewebapi.upload(uploadParam) // Exit the loop and return response on success
      } catch (error) {
        // network issue
        if (error.code === 1054) {
          const RETRY_TIMEOUT_MS = 500
          await sleep(RETRY_TIMEOUT_MS) // Wait before retrying
        } else {
          // unrecoverable error
          console.error('Error in uploadDryRun:', error)
          throw error
        }
      }
    }
  },

  async finalizeUpload(uploadParam, sizeAlreadyUploaded) {
    uploadParam.isDryRun = false
    uploadParam.fileContent = uploadParam.uploadTargetFile.slice(sizeAlreadyUploaded)

    try {
      return await drivewebapi.upload(uploadParam)
    } catch (error) {
      console.error('Error in finalizeUpload:', error)
      return Promise.reject(error)
    }
  },

  async uploadFile(targetId, nodeInfo, conflictAction) {
    const uploadParam = this.prepareUploadParam(targetId, nodeInfo, conflictAction)
    const dryRunResp = await this.uploadDryRun(uploadParam)
    const sizeAlreadyUploaded = dryRunResp?.uploaded_size ?? 0 // sizeAlreadyUploaded is default set to 0 if not provided in dryRunResp

    const resp = await this.finalizeUpload(uploadParam, sizeAlreadyUploaded)
    nodeInfo.name = resp.name // Update nodeInfo with uploaded file name in NAS

    return resp
  },

  async uploadFolder(targetId, nodeInfo, conflictAction) {
    const errorCollection = []
    const folderName = nodeInfo.prefix ? nodeInfo.prefixName : nodeInfo.name

    try {
      // Must create a new folder to meet conflict action policy
      const resp = await drivewebapi.createFolder(targetId, folderName, conflictAction)

      nodeInfo.name = resp.name // Update nodeInfo with uploaded folder name in NAS
      for (const fileInfo of nodeInfo.fileInfos) {
        const updatedNodeInfo = {
          ...fileInfo,
          // Uploading to the new folder makes the first folder name unnecessary.
          shiftedRelativePath: fileInfo.relativePath.split('/').slice(1).join('/')
        }

        try {
          if (fileInfo.isUploadSuccessInFolder !== true) {
            await this.uploadFile(resp.file_id, updatedNodeInfo, conflictAction)
            fileInfo.isUploadSuccessInFolder = true
          }
        } catch (error) {
          error.relativePath = fileInfo.relativePath
          errorCollection.push(error)
        }
      }

      if (errorCollection.length > 0) {
        return Promise.reject(errorCollection)
      }
    } catch (error) {
      console.error('Error in uploadFolder:', error)
      return Promise.reject(error)
    }
  }
}

export default uploadUtils
